"use client";
import { FC, ReactNode, useCallback, useMemo, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { ArrowIcon } from "../icons/ArrowIcon";
import { defaultThemeInRgb } from "../styles/defaultTheme";

interface CarouselProps {
  children: ReactNode[];
}

const iconSizeInPx = 30;
type ArrowSide = "right" | "left";

const Carousel: FC<CarouselProps> = ({ children }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<ArrowSide | null>(null);

  const isInEnd = useMemo(
    () => currentIndex === children.length - 1,
    [children.length, currentIndex]
  );

  const handleArrowClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (side: ArrowSide, e: any) => {
      e?.stopPropagation?.();
      setDirection(side);
      setCurrentIndex((state) => {
        if (side === "right" && !isInEnd) return state + 1;
        if (side === "right" && isInEnd) return state;
        if (side === "left" && currentIndex) return state - 1;
        return state;
      });
    },
    [currentIndex, isInEnd]
  );

  if (children.length === 0) return null;
  return (
    <CarouselWrap>
      {!isInEnd && (
        <CarouselBtnWrap $pos="right">
          <CarouselBtn
            width={iconSizeInPx}
            height={iconSizeInPx}
            onClick={(e) => handleArrowClick("right", e)}
          />
        </CarouselBtnWrap>
      )}
      {children.map((child, i) => (
        <CarouselItem
          direction={direction}
          key={i}
          $displayItem={currentIndex === i}
        >
          {child}
        </CarouselItem>
      ))}
      {!!currentIndex && (
        <CarouselBtnWrap $pos="left">
          <CarouselBtn
            width={iconSizeInPx}
            height={iconSizeInPx}
            onClick={(e) => handleArrowClick("left", e)}
          />
        </CarouselBtnWrap>
      )}
    </CarouselWrap>
  );
};

const slideInFromRight = keyframes`
    0% { transform: translateX(100%); opacity: 0; }
    100% { transform: translateX(0); opacity: 1; }
  `;

const slideInFromLeft = keyframes`
    0% { transform: translateX(-100%); opacity: 0; }
    100% { transform: translateX(0); opacity: 1; }
  `;

const CarouselWrap = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

const CarouselItem = styled.div<{
  direction: ArrowSide | null;
  $displayItem: boolean;
}>`
display: flex;
justify-content: center;
  animation: ${({ direction }) =>
      direction === "right" ? slideInFromRight : slideInFromLeft}
    0.5s ease;
  width: 100%;
  ${({ $displayItem }) =>
    !$displayItem
      ? css`
          display: none;
        `
      : ""}
`;

const CarouselBtnWrap = styled.div<{ $pos: ArrowSide }>`
  position: absolute;
  top: calc(50% - ${iconSizeInPx}px);
  cursor: pointer;
  z-index: 9;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(
    ${defaultThemeInRgb.tertiaryColor.r},
    ${defaultThemeInRgb.tertiaryColor.g},
    ${defaultThemeInRgb.tertiaryColor.b},
    0.5
  );
  border-radius: 50%;
  padding: 0.3rem;
  width: ${iconSizeInPx};
  height: ${iconSizeInPx};
  ${({ $pos }) =>
    $pos === "right"
      ? css`
          right: 0;
        `
      : css`
          left: 0;
          transform: rotate(180deg);
        `}
  &:hover {
    scale: 1.2;
  }
`;
const CarouselBtn = styled(ArrowIcon)`
  /* position: absolute; */
`;

export default Carousel;
